import { CalendarAppointment } from "@/hooks/useCalendarData";
import { format } from "date-fns";
import { de, enUS } from 'date-fns/locale';
import { useTranslation } from "react-i18next";
import { Calendar, Clock, User, ArrowRight, Mail, AlertTriangle, XCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { getTenantTimeZone, toTimeZoneWallClockDate } from "@sylva/shared-types";

export interface Conflict {
  type: 'team_member' | 'resource_capacity' | 'appointment_type_resource' | 'blocked_period';
  severity: 'error' | 'warning';
  message: string;
  details: {
    conflictingAppointment?: CalendarAppointment;
    resourceName?: string;
    teamMemberName?: string;
    requiredResource?: string;
  };
}

export interface Warning {
  type: 'capacity_approaching' | 'back_to_back_appointments';
  message: string;
  details?: Record<string, unknown>;
}

export interface RescheduleConfirmContentProps {
  appointment: CalendarAppointment;
  customerHasEmail?: boolean;
  newStartTime: Date;
  newEndTime: Date;
  timeZone?: string;
  teamMemberName?: string;
  conflicts: Conflict[];
  warnings: Warning[];
  sendNotification: boolean;
  onSendNotificationChange: (value: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  originalStartTime?: Date;
  originalEndTime?: Date;
  showButtons?: boolean;
}

export const RescheduleConfirmContent = ({
  appointment,
  customerHasEmail = true,
  newStartTime,
  newEndTime,
  timeZone,
  teamMemberName,
  conflicts,
  warnings,
  sendNotification,
  onSendNotificationChange,
  onConfirm,
  onCancel,
  originalStartTime,
  originalEndTime,
  showButtons = true,
}: RescheduleConfirmContentProps) => {
  const { t, i18n } = useTranslation();
  const resolvedTimeZone = getTenantTimeZone(timeZone);

  // Use original times from props if provided (for visit drags), otherwise use appointment times
  const currentStartTime = originalStartTime || new Date(appointment.start_time);
  const currentEndTime = originalEndTime || new Date(appointment.end_time);
  const currentStartDisplay = toTimeZoneWallClockDate(currentStartTime, resolvedTimeZone);
  const currentEndDisplay = toTimeZoneWallClockDate(currentEndTime, resolvedTimeZone);
  const newStartDisplay = toTimeZoneWallClockDate(newStartTime, resolvedTimeZone);
  const newEndDisplay = toTimeZoneWallClockDate(newEndTime, resolvedTimeZone);

  // Get the appropriate locale for date-fns formatting
  const locale = i18n.language === 'de' ? de : enUS;

  // Format dates using the preferred pattern from the application
  const formatDate = (date: Date) => {
    return format(date, t('dateFormatShort'), { locale });
  };

  // Check if there are any blocking conflicts
  const hasErrors = conflicts.some(c => c.severity === 'error');
  const errorConflicts = conflicts.filter(c => c.severity === 'error');
  const warningConflicts = conflicts.filter(c => c.severity === 'warning');

  return (
    <div className="space-y-6">
      {/* Conflicts Section */}
      {errorConflicts.length > 0 && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-red-100 flex-shrink-0 mt-0.5">
            <XCircle className="h-3.5 w-3.5 text-red-600" />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-red-800">
              {t('schedulingConflicts')}
            </div>
            {errorConflicts.map((conflict, index) => (
              <div key={index} className="text-sm text-red-700">
                • {conflict.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warnings Section */}
      {(warningConflicts.length > 0 || warnings.length > 0) && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-amber-800">
              {t('schedulingWarnings')}
            </div>
            {warningConflicts.map((conflict, index) => (
              <div key={`conflict-${index}`} className="text-sm text-amber-700">
                {conflict.message}
              </div>
            ))}
            {warnings.map((warning, index) => (
              <div key={`warning-${index}`} className="text-sm text-amber-700">
                {warning.message}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notification Toggle */}
      {!hasErrors && (
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${
          !customerHasEmail
            ? 'bg-slate-50 border-slate-200'
            : sendNotification
              ? 'bg-amber-50 border-amber-200'
              : 'bg-green-50 border-green-200'
        }`}>
          <div className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 mt-0.5 ${
            !customerHasEmail
              ? 'bg-slate-100'
              : sendNotification
                ? 'bg-amber-100'
                : 'bg-green-100'
          }`}>
            <Mail className={`h-3.5 w-3.5 ${
              !customerHasEmail
                ? 'text-slate-400'
                : sendNotification
                  ? 'text-amber-600'
                  : 'text-green-600'
            }`} />
          </div>
          <div className="flex-1 space-y-3">
            <div className={`text-sm ${
              !customerHasEmail
                ? 'text-slate-600'
                : sendNotification
                  ? 'text-amber-800'
                  : 'text-green-800'
            }`}>
              {!customerHasEmail
                ? t('rescheduleNotificationDisabled')
                : sendNotification
                  ? t('rescheduleNotificationEnabled')
                  : t('rescheduleNotificationDisabled')}
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={sendNotification}
                onCheckedChange={onSendNotificationChange}
                disabled={!customerHasEmail}
              />
              <span className={`text-sm font-medium ${
                !customerHasEmail
                  ? 'text-slate-500'
                  : sendNotification
                    ? 'text-amber-800'
                    : 'text-green-800'
              }`}>
                {!customerHasEmail
                  ? t('customerHasNoEmail')
                  : sendNotification
                    ? t('notificationEnabled')
                    : t('notificationDisabled')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Appointment Details */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 p-5 rounded-xl border border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-200">
            <User className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <div className="font-semibold text-slate-900">
              {appointment.customer_name}
            </div>
            <div className="text-sm text-slate-600">
              {appointment.appointment_type_name}
            </div>
          </div>
        </div>

        {/* Time Comparison */}
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            {/* Current Time */}
            <div className="flex-1 bg-card p-4 rounded-lg border border-slate-300">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-slate-500" />
                <div className="text-sm font-medium text-slate-600">
                  {t('currentTime')}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium text-slate-800">
                  {formatDate(currentStartDisplay)}
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {format(currentStartDisplay, t('timeFormatHourMinute'), { locale })} - {format(currentEndDisplay, t('timeFormatHourMinute'), { locale })}
                </div>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 flex-shrink-0">
              <ArrowRight className="h-4 w-4 text-primary" />
            </div>

            {/* New Time */}
            <div className="flex-1 bg-primary/5 p-4 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-primary" />
                <div className="text-sm font-medium text-primary/80">
                  {t('newTime')}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-medium text-primary/90">
                  {formatDate(newStartDisplay)}
                </div>
                <div className="text-lg font-semibold text-primary">
                  {format(newStartDisplay, t('timeFormatHourMinute'), { locale })} - {format(newEndDisplay, t('timeFormatHourMinute'), { locale })}
                </div>
              </div>
            </div>
          </div>

          {/* Team Member */}
          {teamMemberName && (
            <div className="flex items-center gap-3 pt-2 border-t border-slate-300">
              <User className="h-4 w-4 text-slate-500" />
              <div className="text-sm">
                <span className="font-medium text-slate-600">
                  {t('teamMember')}:
                </span>{' '}
                <span className="text-slate-900">
                  {teamMemberName}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      {showButtons && (
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onCancel}>
            {t('cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={hasErrors}>
            <Calendar className="h-4 w-4 mr-2" />
            {hasErrors ? t('cannotReschedule') : t('confirmReschedule')}
          </Button>
        </div>
      )}
    </div>
  );
};
